import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { useRef } from 'react';
import { queryClient } from 'utils/query';

/**
 * Hook to manage data source parameter changes, particularly handling
 * defaultFilter updates across renders.
 *
 * This hook is used by both useInfiniteDataSource and usePagedDataSource
 * to ensure consistent behavior when filter parameters change, especially
 * for entity ID changes (like corporation ID).
 *
 * @param defaultFilter The default filter provided to the data source
 * @param setFilter The state setter function for the filter
 * @param name The query name used for cache invalidation
 * @returns void
 */
export function useDataSourceParams<TFilter>(
    defaultFilter: TFilter,
    setFilter: (filter: TFilter) => void,
    name: string | string[]
): void {
    // Track previous filter for comparison
    const prevDefaultFilterRef = useRef(defaultFilter);

    // Use custom hook to skip first render and update internal filter when defaultFilter changes
    // This is crucial for entity ID changes (like corporation ID)
    useEffectSkipFirst(() => {
        // Check if filter has meaningful changes (especially IDs)
        const hasFilterChanged =
            JSON.stringify(prevDefaultFilterRef.current) !==
            JSON.stringify(defaultFilter);

        if (hasFilterChanged) {
            // Update filter state with new defaultFilter
            setFilter(defaultFilter);
            // Reset query cache to force a fresh fetch
            queryClient.resetQueries({
                queryKey: [name],
            });
            // Update prev filter reference
            prevDefaultFilterRef.current = defaultFilter;
        }
    }, [defaultFilter, name, setFilter]);
}
